import { uiStrings as ui } from '@satu/shared'
import { type ReactNode, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate } from 'react-router'
import { useSambutAlamatBaru } from '@/app/shell/PitaPindahAlamat'
import { sessionController } from '@/auth/app'
import { useSurel } from '@/auth/surel'
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog'
import { Reveal } from '@/components/primitives/Reveal'
import { SIBUK } from '@/components/primitives/Wizard'
import { Button } from '@/components/ui/button'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useSesi } from '../../app/store/sesi'
import { useUi } from '../../app/store/ui'
import { AuthCard, TAUTAN } from './AuthCard'
import { kunciDiMasuk } from './kunciMasuk'
import { type HasilMasukLayar, masukAman, teksGagalMasuk } from './masukAman'
import { type TeksPerubahanLain, teksPerubahanLain } from './perubahanLain'
import { tujuanMasuk } from './tujuan'

interface Isian {
  email: string
  kataSandi: string
}

/** "Lupa PIN?" (./LupaPinRoute): the same screen for the locked account only. */
export interface VarianLupaPin {
  subjudul: string
  /** The locked account's email, fixed and read-only; null when this device does not know it. */
  email: string | null
  /** The sentence for a sign-in as another account. */
  akunLain: string
  /** The links under the form, in place of the usual sentence. */
  tautan: ReactNode
  /** Location state for Buat PIN: where the lock caught the person. */
  state: unknown
}

/**
 * Masuk (online): email, kata sandi, one red button. A failure is stated under the
 * fields as a fact; offline is stated the same way. Success leads to Buat PIN on a new
 * device, to the first download when the device has no data yet, else to Beranda. The
 * "Lupa PIN?" variant states why above the fields and always ends on Buat PIN. On a shared
 * device another person's unsent work refuses the sign-in by name and count (spec 15.4);
 * "Buang N perubahan itu", confirmed, signs in again over it with the same answers.
 * Akun & keamanan (spec 2026-09-30, 5): an account step the API asks for (the initial
 * password, the second step) comes before the PIN; "Lupa kata sandi?" is linked when the
 * server can send SATU's e-mail; a sentence another screen carries (`state.pesan`) stands
 * above the fields. A session found open here (an exit of it to Masuk: a step whose session
 * ended, a recovery, Akun's "Masuk lagi", the address typed) is locked first (./kunciMasuk), so
 * "Buka dengan PIN" asks for the PIN. Release 26: opened from the old address's move
 * (`?dari=alamat-lama`), one line says the device signs in again, makes a new PIN and downloads.
 */
export function MasukRoute({ lupaPin }: { lupaPin?: VarianLupaPin }) {
  const navigate = useNavigate()
  const location = useLocation()
  // "Lupa kata sandi?" only when the server can send SATU's e-mail (GET /api/health).
  const surel = useSurel()
  // A sentence another screen carries here (after a recovery: sign in again for the new app).
  const pesanMasuk = (location.state as { pesan?: unknown } | null)?.pesan
  // Release 26: arrived from the old address (`?dari=alamat-lama`): why the device starts empty.
  const sambutan = useSambutAlamatBaru(location.search)
  const status = useSesi((s) => s.status)
  const online = useUi((s) => s.online)
  const [gagal, setGagal] = useState<{ teks: string; tampil: boolean }>({ teks: '', tampil: false })
  const [tertunda, setTertunda] = useState<{ teks: TeksPerubahanLain; isian: Isian } | null>(null)
  const [konfirmasi, setKonfirmasi] = useState(false)
  const [membuang, setMembuang] = useState(false)
  // From the press on: the session this screen's sign-in opens is not one to lock.
  const [masukDariSini, setMasukDariSini] = useState(false)
  const emailTetap = lupaPin?.email != null
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Isian>({ defaultValues: { email: lupaPin?.email ?? '', kataSandi: '' } })
  const adaSesi = status === 'terkunci' || status === 'aktif'
  // A sign-in under way, or the confirmed discard: Masuk reads busy and does nothing.
  const sibuk = isSubmitting || membuang

  useEffect(() => {
    if (kunciDiMasuk(status, masukDariSini)) void sessionController().kunci()
  }, [status, masukDariSini])

  const selesai = (hasil: HasilMasukLayar, isian: Isian) => {
    if (!hasil.ok) {
      if (hasil.alasan === 'dihapus') return
      if (hasil.alasan === 'perubahan_lain') {
        setTertunda({ teks: teksPerubahanLain(hasil.pemilik), isian })
        return
      }
      setGagal({ teks: teksGagalMasuk(hasil.alasan, lupaPin?.akunLain ?? null), tampil: true })
      return
    }
    // Buat PIN, the first download, Beranda, or an account step first (plan 5).
    navigate(tujuanMasuk(hasil.langkah), { replace: true, state: lupaPin?.state })
  }

  const kirim = handleSubmit(async (isian) => {
    if (membuang) return
    setGagal((g) => ({ ...g, tampil: false }))
    setTertunda(null)
    if (!online) {
      setGagal({ teks: ui.masuk.offline, tampil: true })
      return
    }
    const { email, kataSandi } = isian
    setMasukDariSini(true)
    const hasil = await masukAman(() =>
      sessionController().masuk(email.trim(), kataSandi, lupaPin !== undefined),
    )
    selesai(hasil, isian)
  })

  const buang = async () => {
    if (tertunda === null || membuang) return
    if (!online) {
      setKonfirmasi(false)
      setGagal({ teks: ui.masuk.offline, tampil: true })
      return
    }
    const { isian } = tertunda
    setMembuang(true)
    setMasukDariSini(true)
    try {
      const hasil = await masukAman(() =>
        sessionController().masuk(isian.email.trim(), isian.kataSandi, lupaPin !== undefined, {
          buangPerubahanLain: true,
        }),
      )
      setKonfirmasi(false)
      setTertunda(null)
      selesai(hasil, isian)
    } finally {
      setMembuang(false)
    }
  }

  return (
    <AuthCard title={ui.masuk.judul} subtitle={lupaPin?.subjudul}>
      {lupaPin && <p className="font-prose text-prose text-text">{ui.masuk.lupaPin.kalimat}</p>}
      {sambutan === null ? null : (
        <p role="status" data-sambut-alamat-baru="" className="font-prose text-prose text-text">
          {sambutan}
        </p>
      )}
      {typeof pesanMasuk === 'string' ? (
        <p role="status" className="font-prose text-prose text-text">
          {pesanMasuk}
        </p>
      ) : null}
      {/* The guard sits before handleSubmit: a second submit inside it (Enter pressed again)
          would end react-hook-form's isSubmitting while the first sign-in still runs. */}
      <form
        onSubmit={(event) => {
          if (sibuk) {
            event.preventDefault()
            return
          }
          void kirim(event)
        }}
        noValidate
        className="space-y-6"
      >
        <FieldGroup className="gap-4">
          <Field data-invalid={errors.email !== undefined || undefined}>
            <FieldLabel htmlFor="masuk-email">{ui.masuk.email}</FieldLabel>
            {/* Fixed on "Lupa PIN?": read as a fact on the line, still the form's username. */}
            <Input
              id="masuk-email"
              type="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus={!emailTetap}
              readOnly={emailTetap}
              className={emailTetap ? 'border-transparent px-0' : undefined}
              aria-invalid={errors.email !== undefined || undefined}
              aria-describedby="masuk-email-pesan"
              {...register('email', { required: ui.validasi.wajib })}
            />
            <Reveal open={errors.email !== undefined}>
              <p id="masuk-email-pesan" role="alert" className="text-table font-medium text-text">
                {errors.email?.message}
              </p>
            </Reveal>
          </Field>
          <Field data-invalid={errors.kataSandi !== undefined || undefined}>
            <FieldLabel htmlFor="masuk-kata-sandi">{ui.masuk.kataSandi}</FieldLabel>
            <Input
              id="masuk-kata-sandi"
              type="password"
              autoComplete="current-password"
              autoFocus={emailTetap}
              aria-invalid={errors.kataSandi !== undefined || undefined}
              aria-describedby="masuk-kata-sandi-pesan"
              {...register('kataSandi', { required: ui.validasi.wajib })}
            />
            <Reveal open={errors.kataSandi !== undefined}>
              <p
                id="masuk-kata-sandi-pesan"
                role="alert"
                className="text-table font-medium text-text"
              >
                {errors.kataSandi?.message}
              </p>
            </Reveal>
          </Field>
        </FieldGroup>
        <Reveal open={gagal.tampil} className="-mt-2">
          <p role="alert" className="text-table font-medium text-text">
            {gagal.tampil ? gagal.teks : ' '}
          </p>
        </Reveal>
        <Reveal open={tertunda !== null} className="-mt-2">
          <div className="flex flex-col items-start gap-3">
            <p role="alert" className="font-prose text-prose text-text">
              {tertunda?.teks.kalimat ?? ' '}
            </p>
            {tertunda === null ? null : (
              <Button
                type="button"
                variant="outline"
                aria-disabled={membuang || undefined}
                className={SIBUK}
                onClick={() => {
                  if (!membuang) setKonfirmasi(true)
                }}
              >
                {tertunda.teks.tombol}
              </Button>
            )}
          </div>
        </Reveal>
        <div className="flex justify-end">
          {/* Busy keeps the focus: `aria-disabled`, guarded on submit. */}
          <Button type="submit" className={`press ${SIBUK}`} aria-disabled={sibuk || undefined}>
            {isSubmitting ? ui.masuk.memeriksa : ui.masuk.tombol}
          </Button>
        </div>
        {lupaPin?.tautan ?? (
          <p className="font-prose text-prose text-text">
            {ui.masuk.keterangan}
            {adaSesi && (
              <>
                {' '}
                {/* In Masuk's place: Back from the session it opens never lands on Masuk, which
                    would lock it again. */}
                <Link to="/masuk/pin" replace className={TAUTAN}>
                  {ui.masuk.bukaDenganPin}
                </Link>
              </>
            )}
            {surel ? (
              <>
                {' '}
                <Link to="/masuk/lupa-sandi" className={TAUTAN}>
                  {ui.akun.lupaSandi.tautan}
                </Link>
              </>
            ) : null}
          </p>
        )}
      </form>
      <ConfirmDialog
        open={konfirmasi && tertunda !== null}
        onOpenChange={(buka) => {
          if (!membuang) setKonfirmasi(buka)
        }}
        judul={tertunda?.teks.judul ?? ''}
        kalimat={ui.dialog.buangPerubahanLain.kalimat}
        ya={membuang ? ui.masuk.memeriksa : ui.dialog.buangPerubahanLain.ya}
        batal={ui.dialog.buangPerubahanLain.batal}
        destruktif
        sibuk={membuang}
        onYa={buang}
      />
    </AuthCard>
  )
}
